import React, { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

// Generate a short futuristic AI "blip" using Web Audio API
const playFutureSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    // Sweep oscillator (rising sci-fi tone)
    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(420, now);
    osc1.frequency.exponentialRampToValueAtTime(1400, now + 0.28);

    const osc2 = ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(220, now);
    osc2.frequency.exponentialRampToValueAtTime(880, now + 0.28);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);

    // Soft lowpass to make it less harsh
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(2200, now);
    lp.frequency.exponentialRampToValueAtTime(6000, now + 0.28);

    osc1.connect(lp);
    osc2.connect(lp);
    lp.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 0.5);
    osc2.stop(now + 0.5);

    // Sparkle tail (high blip)
    const spark = ctx.createOscillator();
    spark.type = "sine";
    spark.frequency.setValueAtTime(1800, now + 0.18);
    spark.frequency.exponentialRampToValueAtTime(2600, now + 0.42);
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.0001, now + 0.18);
    sg.gain.exponentialRampToValueAtTime(0.08, now + 0.22);
    sg.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    spark.connect(sg);
    sg.connect(ctx.destination);
    spark.start(now + 0.18);
    spark.stop(now + 0.55);

    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch (e) {
    /* noop */
  }
};

export const CircleCTA = ({ onClick, label = "TAKE ME TO THE FUTURE" }) => {
  const rippleRef = useRef(null);

  const handleClick = (e) => {
    playFutureSound();
    // Ripple
    const el = rippleRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const r = document.createElement("span");
      const size = Math.max(rect.width, rect.height) * 1.4;
      r.style.cssText = `position:absolute;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px;width:${size}px;height:${size}px;border-radius:50%;pointer-events:none;background:radial-gradient(circle,rgba(255,255,255,0.55),rgba(0,240,255,0.25) 40%,transparent 70%);transform:scale(0);opacity:1;transition:transform .7s ease-out, opacity .7s ease-out;`;
      el.appendChild(r);
      requestAnimationFrame(() => {
        r.style.transform = "scale(1)";
        r.style.opacity = "0";
      });
      setTimeout(() => r.remove(), 750);
    }
    onClick?.();
  };

  return (
    <button
      ref={rippleRef}
      onClick={handleClick}
      data-testid="circle-cta-take-me-future"
      className="group relative overflow-hidden rounded-full px-10 sm:px-14 py-6 sm:py-7 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#00F0FF]/50 bubble-cta-square"
      aria-label="Take me to the future"
    >
      {/* Holographic top gloss */}
      <span className="holo-gloss" />
      {/* Glossy bottom shadow puddle */}
      <span className="pointer-events-none absolute -bottom-3 left-6 right-6 h-1/3 rounded-full bg-gradient-to-t from-[#7000B0]/70 to-transparent blur-md" />
      {/* Soft outer rings */}
      <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-white/30" />
      <span className="pointer-events-none absolute inset-1 rounded-full ring-1 ring-white/15" />
      {/* AI scan line */}
      <span className="ai-scan-line" />
      {/* Animated water sheen */}
      <span className="pointer-events-none absolute inset-0 rounded-full water-sheen" />

      {/* Content */}
      <span className="relative z-10 flex items-center gap-3">
        <span className="font-display text-base sm:text-lg md:text-xl font-black tracking-[0.2em] uppercase text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)] whitespace-nowrap">
          {label}
        </span>
        <span className="w-9 h-9 rounded-full bg-[#020205]/85 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/30 group-hover:rotate-45 group-hover:scale-110 transition-transform duration-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
          <ArrowUpRight
            className="w-5 h-5 text-[#00F0FF]"
            strokeWidth={2.6}
          />
        </span>
      </span>
    </button>
  );
};

export default CircleCTA;
